/**
 * Landing hero feed «Недавно добавили услуги» (D40; «Недавно присоединились» until 2026-10-28): the query and the selection rules, as pure
 * functions (no React, no SDK), so the rules are testable on their own.
 *
 * A specialist is shown only with at least one published service listing (any listing type from
 * the runtime config except tasks; the card links to their newest one, and the newest one's
 * createdAt orders the feed: D40 amendment 2026-10-27), an uploaded profile photo,
 * a public professional experience of at least MIN_EXPERIENCE characters, an active account and no
 * operator opt-out (`profile.metadata.hideFromHomeFeed: true`, set in Console, case by case). Any
 * uploaded image qualifies, logos and other non-portrait images included (owner D-b 2026-10-28).
 */
import { getServiceListingTypes } from '../../util/serviceSearch';
import { createSlug } from '../../util/urlHelpers';
import { getShortLocation } from '../../components/ListingCard/taskCardHelpers';

export const MIN_CARDS = 4;
export const MAX_CARDS = 12;
export const MIN_EXPERIENCE = 40;
export const MAX_EXPERIENCE = 180;
// The API maximum: one page holds the whole catalogue today (67 listings on 2026-10-28)
export const HERO_FEED_PER_PAGE = 100;

// 3:4 portrait crops (API edge-detection crop): the widest card is 260 CSS px (--heroCardMaxWidth),
// so the 1x variant is 300 wide and the 2x variant 600 (520 needed).
export const HERO_CARD_VARIANT = 'hero-card';
export const HERO_CARD_VARIANT_2X = 'hero-card-2x';
const VARIANTS = {
  [HERO_CARD_VARIANT]: { w: 300, h: 400 },
  [HERO_CARD_VARIANT_2X]: { w: 600, h: 800 },
};

/**
 * listings.query params for the feed: the newest published service listings (createdAt descending,
 * the order key) with their authors and author photos, sparse fields only. Null when the config has no service listing type (then there
 * is nothing to show, and an unfiltered query would include tasks).
 *
 * @param {Object} config runtime configuration
 * @returns {Object|null}
 */
export const heroFeedQueryParams = config => {
  const serviceTypes = getServiceListingTypes(config);
  if (serviceTypes.length === 0) {
    return null;
  }
  return {
    pub_listingType: serviceTypes.join(','),
    sort: 'createdAt',
    perPage: HERO_FEED_PER_PAGE,
    include: ['author', 'author.profileImage'],
    'fields.listing': [
      'title',
      'state',
      'createdAt',
      'publicData.location',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.listingType',
      'publicData.fulfillmentModes',
    ],
    'fields.user': [
      'createdAt',
      'state',
      'banned',
      'deleted',
      'profile.displayName',
      'profile.publicData.professional_experience',
      'profile.metadata.hideFromHomeFeed',
      'profile.metadata.directContactSubscriptionActive',
    ],
    'fields.image': Object.keys(VARIANTS).map(name => `variants.${name}`),
    ...Object.entries(VARIANTS).reduce(
      (params, [name, { w, h }]) => ({
        ...params,
        [`imageVariant.${name}`]: `w:${w};h:${h};fit:crop`,
      }),
      {}
    ),
  };
};

// European country names as the geocoder may store them (English, French, German, Spanish,
// Italian, Portuguese, native forms) → Russian. Cities: CITY_NAMES_RU below.
const COUNTRY_NAMES_RU = {
  Австрия: ['Austria', 'Autriche', 'Österreich', 'Áustria'],
  Албания: ['Albania', 'Albanie', 'Albanien', 'Albânia', 'Shqipëria'],
  Андорра: ['Andorra', 'Andorre'],
  Армения: ['Armenia', 'Arménie', 'Armenien', 'Arménia', 'Armênia'],
  Азербайджан: [
    'Azerbaijan',
    'Azerbaïdjan',
    'Aserbaidschan',
    'Azerbaiyán',
    'Azerbaigian',
    'Azerbaijão',
  ],
  Беларусь: [
    'Belarus',
    'Biélorussie',
    'Weißrussland',
    'Bielorrusia',
    'Bielorussia',
    'Bielorrússia',
  ],
  Бельгия: ['Belgium', 'Belgique', 'Belgien', 'Bélgica', 'Belgio', 'België'],
  'Босния и Герцеговина': [
    'Bosnia and Herzegovina',
    'Bosnie-Herzégovine',
    'Bosnien und Herzegowina',
    'Bosnia y Herzegovina',
    'Bosnia ed Erzegovina',
    'Bósnia e Herzegovina',
  ],
  Болгария: ['Bulgaria', 'Bulgarie', 'Bulgarien', 'Bulgária'],
  Хорватия: ['Croatia', 'Croatie', 'Kroatien', 'Croacia', 'Croazia', 'Croácia', 'Hrvatska'],
  Кипр: ['Cyprus', 'Chypre', 'Zypern', 'Chipre', 'Cipro'],
  Чехия: [
    'Czechia',
    'Czech Republic',
    'Tchéquie',
    'République tchèque',
    'Tschechien',
    'Chequia',
    'República Checa',
    'Cechia',
    'Repubblica Ceca',
    'Chéquia',
    'Česko',
  ],
  Дания: ['Denmark', 'Danemark', 'Dänemark', 'Dinamarca', 'Danimarca', 'Danmark'],
  Эстония: ['Estonia', 'Estonie', 'Estland', 'Estónia', 'Estônia', 'Eesti'],
  Финляндия: ['Finland', 'Finlande', 'Finnland', 'Finlandia', 'Finlândia', 'Suomi'],
  Франция: ['France', 'Frankreich', 'Francia', 'França'],
  Грузия: ['Georgia', 'Géorgie', 'Georgien', 'Geórgia'],
  Германия: ['Germany', 'Allemagne', 'Deutschland', 'Alemania', 'Germania', 'Alemanha'],
  Греция: ['Greece', 'Grèce', 'Griechenland', 'Grecia', 'Grécia', 'Ελλάδα'],
  Венгрия: ['Hungary', 'Hongrie', 'Ungarn', 'Hungría', 'Ungheria', 'Hungria', 'Magyarország'],
  Исландия: ['Iceland', 'Islande', 'Island', 'Islandia', 'Islanda', 'Islândia'],
  Ирландия: ['Ireland', 'Irlande', 'Irland', 'Irlanda', 'Éire'],
  Италия: ['Italy', 'Italie', 'Italien', 'Italia', 'Itália'],
  Косово: ['Kosovo'],
  Латвия: [
    'Latvia',
    'Lettonie',
    'Lettland',
    'Letonia',
    'Lettonia',
    'Letónia',
    'Letônia',
    'Latvija',
  ],
  Лихтенштейн: ['Liechtenstein', 'Listenstaine'],
  Литва: ['Lithuania', 'Lituanie', 'Litauen', 'Lituania', 'Lituânia', 'Lietuva'],
  Люксембург: ['Luxembourg', 'Luxemburg', 'Luxemburgo', 'Lussemburgo'],
  Мальта: ['Malta', 'Malte'],
  Молдова: ['Moldova', 'Moldavie', 'Moldau', 'Moldawien', 'Moldavia', 'Moldávia'],
  Монако: ['Monaco', 'Mónaco'],
  Черногория: ['Montenegro', 'Monténégro', 'Crna Gora'],
  Нидерланды: [
    'Netherlands',
    'The Netherlands',
    'Pays-Bas',
    'Niederlande',
    'Países Bajos',
    'Paesi Bassi',
    'Países Baixos',
    'Nederland',
  ],
  'Северная Македония': [
    'North Macedonia',
    'Macédoine du Nord',
    'Nordmazedonien',
    'Macedonia del Norte',
    'Macedonia del Nord',
    'Macedónia do Norte',
    'Macedônia do Norte',
  ],
  Норвегия: ['Norway', 'Norvège', 'Norwegen', 'Noruega', 'Norvegia', 'Norge'],
  Польша: ['Poland', 'Pologne', 'Polen', 'Polonia', 'Polónia', 'Polônia', 'Polska'],
  Португалия: ['Portugal', 'Portogallo'],
  Румыния: [
    'Romania',
    'Roumanie',
    'Rumänien',
    'Rumania',
    'Rumanía',
    'Roménia',
    'Romênia',
    'România',
  ],
  Россия: ['Russia', 'Russie', 'Russland', 'Rusia', 'Rússia'],
  'Сан-Марино': ['San Marino', 'Saint-Marin', 'São Marinho'],
  Сербия: ['Serbia', 'Serbie', 'Serbien', 'Sérvia', 'Srbija'],
  Словакия: [
    'Slovakia',
    'Slovaquie',
    'Slowakei',
    'Eslovaquia',
    'Slovacchia',
    'Eslováquia',
    'Slovensko',
  ],
  Словения: [
    'Slovenia',
    'Slovénie',
    'Slowenien',
    'Eslovenia',
    'Eslovénia',
    'Eslovênia',
    'Slovenija',
  ],
  Испания: ['Spain', 'Espagne', 'Spanien', 'España', 'Spagna', 'Espanha'],
  Швеция: ['Sweden', 'Suède', 'Schweden', 'Suecia', 'Svezia', 'Suécia', 'Sverige'],
  Швейцария: ['Switzerland', 'Suisse', 'Schweiz', 'Suiza', 'Svizzera', 'Suíça'],
  Турция: ['Turkey', 'Türkiye', 'Turquie', 'Türkei', 'Turquía', 'Turchia', 'Turquia'],
  Украина: ['Ukraine', 'Ucrania', 'Ucraina', 'Ucrânia', 'Україна'],
  Великобритания: [
    'United Kingdom',
    'UK',
    'Great Britain',
    'Royaume-Uni',
    'Vereinigtes Königreich',
    'Großbritannien',
    'Reino Unido',
    'Regno Unito',
  ],
  Ватикан: [
    'Vatican',
    'Vatican City',
    'Cité du Vatican',
    'Vatikanstadt',
    'Ciudad del Vaticano',
    'Città del Vaticano',
    'Vaticano',
  ],
};

// Lower case, without accents or apostrophe variants, so «Espagne», «ESPAGNE» and «Géorgie» /
// «Georgie» match the same entry (countries and cities alike).
const placeKey = name =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const COUNTRY_LOOKUP = Object.entries(COUNTRY_NAMES_RU).reduce((lookup, [ru, names]) => {
  names.forEach(name => {
    lookup[placeKey(name)] = ru;
  });
  return lookup;
}, {});

/**
 * Russian name of a European country given in another language; any other value is returned
 * unchanged (Russian names, non-European countries, unknown places).
 *
 * @param {string} name e.g. "Espagne"
 * @returns {string} e.g. "Испания"
 */
export const normalizeCountryName = name => {
  if (typeof name !== 'string' || !name.trim()) {
    return '';
  }
  return COUNTRY_LOOKUP[placeKey(name)] || name.trim();
};

// European cities (and a few regions the geocoder returns as the place) as stored in French,
// English, Italian, Spanish, German or the local language → Russian. Names that differ by country
// («Valence»: Валенсия in Spain, Валанс in France) are in CITY_NAMES_RU_BY_COUNTRY only.
const CITY_NAMES_RU = {
  // Spain
  Барселона: ['Barcelona', 'Barcelone'],
  Мадрид: ['Madrid'],
  Валенсия: ['Valencia', 'València'],
  Аликанте: ['Alicante', 'Alacant'],
  Малага: ['Málaga', 'Malaga'],
  Марбелья: ['Marbella'],
  Севилья: ['Sevilla', 'Seville', 'Séville'],
  Бильбао: ['Bilbao'],
  Сарагоса: ['Zaragoza', 'Saragosse'],
  'Пальма-де-Майорка': ['Palma', 'Palma de Mallorca', 'Palma de Majorque'],
  Торревьеха: ['Torrevieja'],
  Бенидорм: ['Benidorm'],
  'Льорет-де-Мар': ['Lloret de Mar'],
  Таррагона: ['Tarragona', 'Tarragone'],
  Жирона: ['Girona', 'Gérone'],
  Мурсия: ['Murcia', 'Murcie'],
  'Лас-Пальмас-де-Гран-Канария': ['Las Palmas de Gran Canaria', 'Las Palmas'],
  // Italy
  Рим: ['Rome', 'Roma', 'Rom'],
  Милан: ['Milan', 'Milano', 'Mailand'],
  Флоренция: ['Florence', 'Firenze', 'Florenz'],
  Турин: ['Turin', 'Torino'],
  Неаполь: ['Naples', 'Napoli', 'Neapel'],
  Венеция: ['Venice', 'Venise', 'Venezia', 'Venedig'],
  Болонья: ['Bologna', 'Bologne'],
  Генуя: ['Genoa', 'Gênes', 'Genova', 'Genua'],
  Палермо: ['Palermo', 'Palerme'],
  Монреале: ['Monreale'],
  Верона: ['Verona', 'Vérone'],
  Пиза: ['Pisa', 'Pise'],
  Римини: ['Rimini'],
  Бари: ['Bari'],
  Катания: ['Catania', 'Catane'],
  Виареджо: ['Viareggio'],
  Тоскана: ['Toscane', 'Tuscany', 'Toscana', 'Toskana'],
  Сицилия: ['Sicile', 'Sicily', 'Sicilia', 'Sizilien'],
  // France
  Париж: ['Paris'],
  Марсель: ['Marseille', 'Marseilles'],
  Лион: ['Lyon', 'Lyons'],
  Ницца: ['Nice', 'Nizza', 'Niza'],
  Тулуза: ['Toulouse'],
  Бордо: ['Bordeaux'],
  Страсбург: ['Strasbourg', 'Straßburg'],
  Монпелье: ['Montpellier'],
  Нант: ['Nantes'],
  Лилль: ['Lille'],
  Канны: ['Cannes'],
  Антиб: ['Antibes'],
  Ментона: ['Menton', 'Mentone'],
  Гренобль: ['Grenoble'],
  // Portugal
  Лиссабон: ['Lisbon', 'Lisboa', 'Lisbonne', 'Lissabon'],
  Порту: ['Porto', 'Oporto'],
  Фару: ['Faro'],
  Кашкайш: ['Cascais'],
  // Germany, Austria, Switzerland
  Берлин: ['Berlin'],
  Мюнхен: ['Munich', 'München'],
  Гамбург: ['Hamburg', 'Hambourg', 'Amburgo'],
  'Франкфурт-на-Майне': ['Frankfurt', 'Frankfurt am Main', 'Francfort', 'Francfort-sur-le-Main'],
  Кёльн: ['Cologne', 'Köln', 'Colonia'],
  Дюссельдорф: ['Düsseldorf', 'Dusseldorf'],
  Штутгарт: ['Stuttgart'],
  Вена: ['Vienna', 'Vienne', 'Wien', 'Viena'],
  Цюрих: ['Zurich', 'Zürich', 'Zurigo'],
  Женева: ['Geneva', 'Genève', 'Genf', 'Ginevra', 'Ginebra'],
  // Benelux, UK, Ireland
  Амстердам: ['Amsterdam'],
  Роттердам: ['Rotterdam'],
  Гаага: ['The Hague', 'La Haye', 'Den Haag'],
  Брюссель: ['Brussels', 'Bruxelles', 'Brussel', 'Brüssel', 'Bruselas', 'Bruxelas'],
  Антверпен: ['Antwerp', 'Anvers', 'Antwerpen'],
  Лондон: ['London', 'Londres', 'Londra'],
  Дублин: ['Dublin'],
  // Central and Eastern Europe, Baltics, Nordics
  Варшава: ['Warsaw', 'Warszawa', 'Varsovie', 'Warschau', 'Varsavia', 'Varsovia'],
  Краков: ['Krakow', 'Kraków', 'Cracow', 'Cracovie', 'Krakau', 'Cracovia'],
  Гданьск: ['Gdansk', 'Gdańsk', 'Dantzig', 'Danzig'],
  Вроцлав: ['Wroclaw', 'Wrocław', 'Breslau'],
  Познань: ['Poznan', 'Poznań'],
  Прага: ['Prague', 'Praha', 'Prag', 'Praga'],
  Будапешт: ['Budapest'],
  Белград: ['Belgrade', 'Beograd', 'Belgrad'],
  Будва: ['Budva'],
  Подгорица: ['Podgorica'],
  Рига: ['Riga', 'Rīga'],
  Вильнюс: ['Vilnius'],
  Таллин: ['Tallinn'],
  Хельсинки: ['Helsinki', 'Helsingfors'],
  Стокгольм: ['Stockholm', 'Stoccolma', 'Estocolmo'],
  Копенгаген: ['Copenhagen', 'Copenhague', 'København', 'Kopenhagen'],
  Осло: ['Oslo'],
  // South-east Europe, Cyprus, Caucasus, Turkey
  Афины: ['Athens', 'Athènes', 'Athen', 'Atene', 'Atenas', 'Αθήνα'],
  Салоники: ['Thessaloniki', 'Thessalonique', 'Salonicco'],
  Лимасол: ['Limassol', 'Lemesos'],
  Ларнака: ['Larnaca', 'Larnaka'],
  Пафос: ['Paphos', 'Pafos'],
  Никосия: ['Nicosia', 'Nicosie', 'Lefkosia'],
  Тбилиси: ['Tbilisi'],
  Батуми: ['Batumi'],
  Ереван: ['Yerevan', 'Erevan', 'Jerewan'],
  Стамбул: ['Istanbul', 'İstanbul', 'Estambul'],
  Анталья: ['Antalya'],
};

const CITY_NAMES_RU_BY_COUNTRY = {
  Испания: { Валенсия: ['Valence'] },
  Франция: { Валанс: ['Valence'] },
};

const toLookup = dictionary =>
  Object.entries(dictionary).reduce((lookup, [ru, names]) => {
    names.forEach(name => {
      lookup[placeKey(name)] = ru;
    });
    return lookup;
  }, {});

const CITY_LOOKUP = toLookup(CITY_NAMES_RU);
const CITY_LOOKUP_BY_COUNTRY = Object.entries(CITY_NAMES_RU_BY_COUNTRY).reduce(
  (lookup, [country, dictionary]) => ({ ...lookup, [country]: toLookup(dictionary) }),
  {}
);

/**
 * Russian name of a European city given in another language (country-specific names first, when
 * the country is known); any other value is returned unchanged.
 *
 * @param {string} name e.g. "Barcelone"
 * @param {string} [countryRu] the country in Russian (normalizeCountryName), e.g. "Испания"
 * @returns {string} e.g. "Барселона"
 */
export const normalizeCityName = (name, countryRu) => {
  if (typeof name !== 'string' || !name.trim()) {
    return '';
  }
  const key = placeKey(name);
  return CITY_LOOKUP_BY_COUNTRY[countryRu]?.[key] || CITY_LOOKUP[key] || name.trim();
};

/**
 * Card place: the city from getShortLocation (never a street), or the country when the address has
 * no city (online-only specialists often give the country only). European city and country names
 * are shown in Russian; unknown names as stored.
 *
 * @param {string} address geocoded address (publicData.location.address)
 * @returns {string} e.g. "Мадрид" or "Испания", '' when nothing usable
 */
export const getHeroPlace = address => {
  const short = getShortLocation(address);
  if (!short) {
    return '';
  }
  const parts = short.split(', ');
  // «Город, Страна» → the city; a single part is a country (or a lone place name)
  if (parts.length > 1) {
    return normalizeCityName(parts[0], normalizeCountryName(parts[parts.length - 1]));
  }
  const country = normalizeCountryName(parts[0]);
  return country !== parts[0] ? country : normalizeCityName(parts[0]);
};

const collapseWhitespace = text =>
  typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';

/**
 * Experience text for the card: whitespace collapsed, cut at a word boundary to MAX_EXPERIENCE
 * characters with an ellipsis (the card clamps it to two lines; the cut keeps the SSR HTML small).
 *
 * @param {string} text
 * @returns {string}
 */
export const normalizeExperience = text => {
  const clean = collapseWhitespace(text);
  if (clean.length <= MAX_EXPERIENCE) {
    return clean;
  }
  const cut = clean.slice(0, MAX_EXPERIENCE);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > MAX_EXPERIENCE / 2 ? cut.slice(0, lastSpace) : cut;
  return `${base.replace(/[\s.,;:!?—–-]+$/, '')}…`;
};

// Countries where the card shows the city (or the country); anywhere else it shows «Онлайн».
const PLACE_COUNTRIES = new Set(
  Object.keys(COUNTRY_NAMES_RU).filter(ru => !['Россия', 'Украина', 'Беларусь'].includes(ru))
);

/**
 * Card location: «Онлайн» (online: true) for an online-only listing (fulfillmentModes is exactly
 * ['online']) and for any country outside PLACE_COUNTRIES (Russia, Ukraine, Belarus, outside
 * Europe); otherwise the place from getHeroPlace (city, or country when there is no city).
 *
 * @param {string} address geocoded address (publicData.location.address)
 * @param {Array<string>} fulfillmentModes publicData.fulfillmentModes ('online', 'offline')
 * @returns {{ place: string, online: boolean }}
 */
export const getHeroLocation = (address, fulfillmentModes) => {
  const modes = Array.isArray(fulfillmentModes) ? fulfillmentModes : [];
  const isOnlineOnly = modes.length > 0 && modes.every(m => m === 'online');
  const short = getShortLocation(address);
  const parts = short ? short.split(', ') : [];
  // The last part is the country (a single part is the country of an address without a city)
  const country = normalizeCountryName(parts[parts.length - 1]);
  const isOutsidePlaceCountries = parts.length > 0 && !PLACE_COUNTRIES.has(country);
  return isOnlineOnly || isOutsidePlaceCountries
    ? { place: '', online: true }
    : { place: getHeroPlace(address), online: false };
};

// The catch-all «Другое» subcategory of every category (ids end in _OTH): it says nothing on a card.
const isOtherCategory = category =>
  /_OTH$/i.test(category?.id || '') || placeKey(category?.name || '') === 'другое';

// Level-2 category name, or level 1 when level 2 is missing, unknown or «Другое».
export const getHeroCategory = (publicData, categoryConfiguration) => {
  const level1 = (categoryConfiguration?.categories || []).find(
    c => c?.id === publicData?.categoryLevel1
  );
  const level2 = (level1?.subcategories || []).find(c => c?.id === publicData?.categoryLevel2);
  return (level2 && !isOtherCategory(level2) ? level2.name : '') || level1?.name || '';
};

// Any uploaded profile image qualifies, whatever it shows (logo, badge, non-portrait: owner D-b
// 2026-10-28): the 3:4 crops when the API returns them, otherwise the widest other variant of the
// same image. The card crops whatever it gets to 3:4 (object-fit: cover).
const getHeroImage = profileImage => {
  const variants = profileImage?.attributes?.variants || {};
  const hasUrl = v => !!v?.url;
  const crops = [variants[HERO_CARD_VARIANT], variants[HERO_CARD_VARIANT_2X]].filter(hasUrl);
  const widestOther = Object.values(variants)
    .filter(hasUrl)
    .sort((a, b) => (b.width || 0) - (a.width || 0))
    .slice(0, 1);
  const candidates = crops.length > 0 ? crops : widestOther;
  if (candidates.length === 0) {
    return null;
  }
  const srcSet = candidates
    .filter(v => v.width)
    .map(v => `${v.url} ${v.width}w`)
    .join(', ');
  return { src: candidates[0].url, srcSet };
};

const isActiveAuthor = author => {
  const { state, banned, deleted } = author?.attributes || {};
  return state === 'active' && banned !== true && deleted !== true;
};

const toTime = date => {
  const time = date instanceof Date ? date.getTime() : new Date(date).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Feed cards from denormalised listings (each with `author` and `author.profileImage`): one card
 * per author (their newest published service listing by listing createdAt; on equal or missing
 * dates the first one in API order), filtered by the rules above, ordered by that listing's
 * createdAt, newest first (D40 amendment 2026-10-27: publishing a new service moves its author to
 * the front; editing, closing or reopening keeps createdAt and the place), at most MAX_CARDS.
 *
 * @param {Array<Object>} listings denormalised listing entities
 * @param {Object} options
 * @param {Array<string>} options.serviceTypes from getServiceListingTypes
 * @param {Object} options.categoryConfiguration config.categoryConfiguration
 * @returns {Array<{ listingId: string, slug: string, name: string, place: string, online: boolean,
 *   category: string, experience: string, image: { src: string, srcSet: string } }>}
 */
export const selectHeroSpecialists = (listings, options = {}) => {
  const { serviceTypes = [], categoryConfiguration } = options;
  if (!Array.isArray(listings)) {
    return [];
  }

  // The newest published service listing of every author. Never drafts, closed listings or tasks.
  const newestByAuthor = new Map();
  listings.forEach(listing => {
    const attributes = listing?.attributes || {};
    const authorId = listing?.author?.id?.uuid;
    const isPublishedService =
      attributes.state === 'published' && serviceTypes.includes(attributes.publicData?.listingType);
    if (!authorId || !isPublishedService) {
      return;
    }
    const createdAt = toTime(attributes.createdAt);
    const newest = newestByAuthor.get(authorId);
    if (!newest || createdAt > newest.createdAt) {
      newestByAuthor.set(authorId, { listing, createdAt });
    }
  });

  const cards = [];
  newestByAuthor.forEach(({ listing, createdAt }) => {
    const attributes = listing.attributes;
    const publicData = attributes.publicData || {};
    const author = listing.author;
    const profile = author.attributes?.profile || {};
    const metadata = profile.metadata || {};
    const experience = profile.publicData?.professional_experience;
    const image = getHeroImage(author.profileImage);
    const name = (profile.displayName || '').trim();

    const isShown =
      isActiveAuthor(author) &&
      metadata.hideFromHomeFeed !== true &&
      metadata.directContactSubscriptionActive !== false &&
      !!image &&
      !!name &&
      collapseWhitespace(experience).length >= MIN_EXPERIENCE;
    if (!isShown) {
      return;
    }

    cards.push({
      createdAt,
      card: {
        listingId: listing.id.uuid,
        slug: createSlug(attributes.title || ''),
        name,
        ...getHeroLocation(publicData.location?.address, publicData.fulfillmentModes),
        category: getHeroCategory(publicData, categoryConfiguration),
        experience: normalizeExperience(experience),
        image,
      },
    });
  });

  return cards
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_CARDS)
    .map(({ card }) => card);
};
